import { Badge, Box, Divider, Group, Stack, Table, Text } from '@mantine/core';
import { Fragment, useMemo } from 'react';

import { DEPARTMENTS_BY_SECTION, SECTIONS } from '../constants/pageAccess';
import { roleTypes } from '../types';
import { PAGE_ACCESS_SECTION_ICONS, PLATFORM_ICONS } from '../utils/icons';

const PLATFORM_LABELS = [
  { key: 'instagram', label: 'IG' },
  { key: 'telegram', label: 'TG' },
] as const;

type Props = {
  pageAccess: roleTypes.Role['pageAccess'];
};

export const RolePageAccessCell = ({ pageAccess }: Props) => {
  const sections = useMemo(() => {
    if (!pageAccess) {
      return [];
    }
    const result: {
      label: string;
      sectionKey: keyof typeof DEPARTMENTS_BY_SECTION;
      platforms: { key: string; label: string; departments: string[] }[];
    }[] = [];
    for (const section of SECTIONS) {
      const validDepartments = DEPARTMENTS_BY_SECTION[section.key];
      const platforms = PLATFORM_LABELS.map((platform) => ({
        key: platform.key,
        label: platform.label,
        departments: (pageAccess[section.key]?.[platform.key] ?? []).filter((department) =>
          validDepartments.includes(department)
        ),
      })).filter((platform) => platform.departments.length > 0);
      if (platforms.length > 0) {
        result.push({ label: section.label, sectionKey: section.key, platforms });
      }
    }
    return result;
  }, [pageAccess]);

  return (
    <Table.Td>
      {sections.length > 0 ? (
        <Stack gap={4}>
          {sections.map(({ label: sectionLabel, sectionKey, platforms }) => {
            const totalDepartments = DEPARTMENTS_BY_SECTION[sectionKey].length;
            return (
              <Fragment key={sectionLabel}>
                <Divider
                  my={2}
                  label={
                    <Group gap={4} align="center">
                      {PAGE_ACCESS_SECTION_ICONS[sectionLabel] && (
                        <Box style={{ opacity: 0.5, display: 'flex' }}>
                          {PAGE_ACCESS_SECTION_ICONS[sectionLabel](12)}
                        </Box>
                      )}
                      <Text size="xs" c="dimmed" fw={500}>
                        {sectionLabel}
                      </Text>
                    </Group>
                  }
                  labelPosition="left"
                />
                {platforms.map(({ key, departments }) => (
                  <Group key={key} gap={4} wrap="nowrap" align="flex-start">
                    <Box w={18} style={{ flexShrink: 0, opacity: 0.4, paddingTop: 1 }}>
                      {PLATFORM_ICONS[key](14)}
                    </Box>
                    {departments.length >= totalDepartments ? (
                      <Badge size="xs" variant="light" color="violet" radius="sm">
                        All
                      </Badge>
                    ) : (
                      <Group gap={3} wrap="wrap">
                        {departments.map((department) => (
                          <Badge
                            key={department}
                            size="xs"
                            variant="light"
                            color="violet"
                            radius="sm"
                          >
                            {department.toUpperCase()}
                          </Badge>
                        ))}
                      </Group>
                    )}
                  </Group>
                ))}
              </Fragment>
            );
          })}
        </Stack>
      ) : (
        <Text ta="center" c="dimmed" size="sm">
          —
        </Text>
      )}
    </Table.Td>
  );
};
